/**
 * Message Channel Manager for Assets SDK
 * Handles MessageChannel setup and communication for workers and cross-origin iframes
 */
import { generateUniqueId, getWorkerById, removeWorkerFromRegistry, getRegistrationChannelName } from './worker-manager';
export class MessageChannelManager {
    constructor(options, allowedOrigins = []) {
        Object.defineProperty(this, "messageChannels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        }); // iframe channels
        Object.defineProperty(this, "workers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        }); // worker channels
        Object.defineProperty(this, "registrationChannel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "postMessageListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "allowedOrigins", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "options", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Worker-side properties
        Object.defineProperty(this, "messagePort", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "workerMessageHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "workerTimeoutId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "workerPromiseReject", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "workerId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.options = options;
        this.allowedOrigins = allowedOrigins;
    }
    /**
     * Sets up parent-side MessageChannel proxy for cross-origin iframes
     */
    setupParentMessageChannelProxy() {
        if (typeof window === 'undefined') {
            return;
        }
        this.postMessageListener = (event) => {
            // Only handle iframe initialization requests
            if (event.data?.type !== 'ASSETS_SDK_INIT') {
                return;
            }
            // Validate origin
            if (!this.allowedOrigins.includes(event.origin)) {
                console.warn(`[Assets SDK Parent] Rejected connection from unauthorized origin: ${event.origin}`);
                console.warn(`[Assets SDK Parent] Allowed origins:`, this.allowedOrigins);
                return;
            }
            // Validate source
            if (!event.source || !event.source.postMessage) {
                console.warn('[Assets SDK Parent] Invalid event source');
                return;
            }
            // Origin validated, create MessageChannel
            // Create MessageChannel for this iframe
            const channel = new MessageChannel();
            const channelId = generateUniqueId();
            // Set up message handler for asset requests from iframe
            channel.port1.onmessage = async (msgEvent) => {
                if (msgEvent.data?.type === 'ASSET_REQUEST') {
                    const { messageId, requestParam, token, options } = msgEvent.data;
                    // Validate input
                    if (!requestParam || typeof requestParam !== 'object') {
                        channel.port1.postMessage({
                            type: 'ASSET_RESPONSE',
                            messageId,
                            error: 'Invalid request parameters'
                        });
                        return;
                    }
                    if (!requestParam.rxId || !requestParam.assetReferenceType) {
                        channel.port1.postMessage({
                            type: 'ASSET_RESPONSE',
                            messageId,
                            error: 'Missing required parameters (rxId or assetReferenceType)'
                        });
                        return;
                    }
                    // Send ACK immediately
                    channel.port1.postMessage({
                        type: 'ACK',
                        messageId
                    });
                    try {
                        const asset = await this.options.getAsset(requestParam, token, options);
                        // Use transferables or structured clone based on flag
                        if (this.options.useTransferables) {
                            channel.port1.postMessage({
                                type: 'ASSET_RESPONSE',
                                messageId,
                                asset,
                                wasTransferred: true
                            }, [asset]);
                            // Invalidate cache (transferred away)
                            this.options.cacheManager.delete(requestParam.rxId, requestParam.assetReferenceType.toString());
                        }
                        else {
                            channel.port1.postMessage({
                                type: 'ASSET_RESPONSE',
                                messageId,
                                asset,
                                wasTransferred: false
                            });
                        }
                    }
                    catch (error) {
                        const errorMessage = error.message || 'Unknown error';
                        console.error(`[Assets SDK Parent] Error processing iframe asset request - messageId: ${messageId}, error: ${errorMessage}`);
                        channel.port1.postMessage({
                            type: 'ASSET_RESPONSE',
                            messageId,
                            error: errorMessage
                        });
                    }
                }
                else if (msgEvent.data?.type === 'RETURN_ASSET') {
                    // Handle asset return from iframe on dispose
                    const { cacheKey, asset, returnId } = msgEvent.data;
                    // Extract rxId and assetType from cacheKey (format: rxId-assetType)
                    const lastDashIndex = cacheKey.lastIndexOf('-');
                    const rxId = cacheKey.substring(0, lastDashIndex);
                    const assetType = cacheKey.substring(lastDashIndex + 1);
                    this.options.cacheManager.add(rxId, assetType, asset);
                    // Send ACK back to child
                    if (returnId) {
                        channel.port1.postMessage({ type: 'RETURN_ACK', returnId });
                    }
                }
            };
            channel.port1.start();
            // Store port1 for parent to send messages
            this.messageChannels.set(channelId, channel.port1);
            // Send port2 to iframe
            event.source.postMessage({ type: 'ASSETS_SDK_READY', port: channel.port2 }, event.origin, [channel.port2]);
        };
        window.addEventListener('message', this.postMessageListener);
    }
    /**
     * Sets up parent-side worker registration listener
     */
    setupWorkerRegistrationListener() {
        if (typeof BroadcastChannel === 'undefined') {
            return;
        }
        // Uses unique channel name to prevent cross-tab interference
        this.registrationChannel = new BroadcastChannel(getRegistrationChannelName());
        this.registrationChannel.onmessage = async (event) => {
            if (event.data?.type === 'WORKER_REGISTER') {
                const workerId = event.data.workerId;
                // Retrieve worker from module-level registry
                const worker = getWorkerById(workerId);
                if (!worker) {
                    console.error(`[Assets SDK] Worker not found in registry (ID: ${workerId})`);
                    return;
                }
                // Create MessageChannel for this worker
                const channel = new MessageChannel();
                // Set up message handler for asset requests from worker
                channel.port1.onmessage = async (msgEvent) => {
                    if (msgEvent.data?.type === 'ASSET_REQUEST') {
                        const { messageId, requestParam, token, options } = msgEvent.data;
                        // Validate input
                        if (!requestParam || typeof requestParam !== 'object') {
                            console.warn(`[Assets SDK] Invalid request parameters from worker (ID: ${workerId})`);
                            channel.port1.postMessage({
                                type: 'ASSET_RESPONSE',
                                messageId,
                                error: 'Invalid request parameters'
                            });
                            return;
                        }
                        if (!requestParam.rxId || !requestParam.assetReferenceType) {
                            console.warn(`[Assets SDK] Missing required parameters from worker (ID: ${workerId})`);
                            channel.port1.postMessage({
                                type: 'ASSET_RESPONSE',
                                messageId,
                                error: 'Missing required parameters (rxId or assetReferenceType)'
                            });
                            return;
                        }
                        // Send ACK immediately
                        channel.port1.postMessage({
                            type: 'ACK',
                            messageId
                        });
                        try {
                            const asset = await this.options.getAsset(requestParam, token, options);
                            // Use transferables or structured clone based on flag
                            if (this.options.useTransferables) {
                                channel.port1.postMessage({
                                    type: 'ASSET_RESPONSE',
                                    messageId,
                                    asset,
                                    wasTransferred: true
                                }, [asset]);
                                // Invalidate cache (transferred away)
                                this.options.cacheManager.delete(requestParam.rxId, requestParam.assetReferenceType.toString());
                            }
                            else {
                                channel.port1.postMessage({
                                    type: 'ASSET_RESPONSE',
                                    messageId,
                                    asset,
                                    wasTransferred: false
                                });
                            }
                        }
                        catch (error) {
                            const errorMessage = error.message || 'Unknown error';
                            console.error(`[Assets SDK Parent] Error processing worker asset request - messageId: ${messageId}, error: ${errorMessage}`);
                            channel.port1.postMessage({
                                type: 'ASSET_RESPONSE',
                                messageId,
                                error: errorMessage
                            });
                        }
                    }
                    else if (msgEvent.data?.type === 'RETURN_ASSET') {
                        // Handle asset return from worker on dispose
                        const { cacheKey, asset, returnId } = msgEvent.data;
                        // Extract rxId and assetType from cacheKey (format: rxId-assetType)
                        const lastDashIndex = cacheKey.lastIndexOf('-');
                        const rxId = cacheKey.substring(0, lastDashIndex);
                        const assetType = cacheKey.substring(lastDashIndex + 1);
                        this.options.cacheManager.add(rxId, assetType, asset);
                        // Send ACK back to child
                        if (returnId) {
                            channel.port1.postMessage({ type: 'RETURN_ACK', returnId });
                        }
                    }
                    else if (msgEvent.data?.type === 'WORKER_UNREGISTER') {
                        // Close and remove the worker's port
                        channel.port1.close();
                        this.workers.delete(workerId);
                        // Also remove from worker registry
                        removeWorkerFromRegistry(workerId);
                    }
                };
                channel.port1.start();
                // Store port1 for parent
                this.workers.set(workerId, channel.port1);
                // Send port2 to worker via worker.postMessage (supports transferables!)
                worker.postMessage({
                    type: 'ASSETS_SDK_READY',
                    port: channel.port2,
                    workerId: workerId // Send workerId so worker can auto-unregister on dispose
                }, [channel.port2]);
            }
            else if (event.data?.type === 'WORKER_UNREGISTER') {
                const workerId = event.data.workerId;
                // Close and remove the worker's port
                const port = this.workers.get(workerId);
                if (port) {
                    port.close();
                    this.workers.delete(workerId);
                    // Also remove from worker registry
                    removeWorkerFromRegistry(workerId);
                }
                else {
                    console.warn(`[Assets SDK] Worker port not found during unregistration (ID: ${workerId})`);
                }
            }
        };
    }
    /**
     * Child-side: Waits for MessageChannel port from parent (worker context)
     */
    async autoRegisterWorker(returnAssetsCallback) {
        return new Promise((resolve, reject) => {
            // Store reject function for cleanup
            this.workerPromiseReject = reject;
            this.workerMessageHandler = (event) => {
                if (event.data?.type === 'ASSETS_SDK_READY') {
                    // Receive port and workerId from parent
                    this.messagePort = event.data.port;
                    this.workerId = event.data.workerId;
                    if (this.messagePort) {
                        this.messagePort.start();
                        // Set up persistent listener for parent requests
                        this.messagePort.addEventListener('message', async (msgEvent) => {
                            if (msgEvent.data?.type === 'REQUEST_ASSET_RETURN') {
                                // Parent is requesting asset return
                                const { retrievalId } = msgEvent.data;
                                // Return all transferred assets
                                await returnAssetsCallback();
                                // Send completion notification
                                this.messagePort?.postMessage({
                                    type: 'RETURN_COMPLETE',
                                    retrievalId
                                });
                            }
                        });
                    }
                    // Clear timeout and cleanup
                    if (this.workerTimeoutId) {
                        clearTimeout(this.workerTimeoutId);
                        this.workerTimeoutId = undefined;
                    }
                    // Don't remove listener - keep it for ASSETS_SDK_DISPOSE
                    this.workerPromiseReject = undefined;
                    resolve(this.messagePort);
                }
                else if (event.data?.type === 'ASSETS_SDK_DISPOSE') {
                    // Parent requested graceful disposal
                    // The dispose will be handled by the main Assets class
                }
            };
            self.addEventListener('message', this.workerMessageHandler);
            // Timeout after 5 seconds
            this.workerTimeoutId = setTimeout(() => {
                console.error('[Assets SDK Worker] Port initialization timeout - did you call Assets.registerWorker(worker)?');
                // Don't remove listener on timeout - keep it for potential dispose signal
                this.workerTimeoutId = undefined;
                this.workerPromiseReject = undefined;
                reject(new Error('Worker port initialization timeout - ensure Assets.registerWorker() was called'));
            }, 5000);
        });
    }
    /**
     * Child-side: Initializes MessageChannel connection for cross-origin iframe
     */
    async initializeChildConnection(returnAssetsCallback) {
        return new Promise((resolve, reject) => {
            if (typeof window === 'undefined' || window.parent === window) {
                reject(new Error('Not in iframe context'));
                return;
            }
            let timeoutId;
            const readyListener = (event) => {
                if (event.data?.type === 'ASSETS_SDK_READY') {
                    // Validate it came from parent window
                    if (event.source !== window.parent) {
                        console.warn('[Assets SDK Child] Rejected port from non-parent source');
                        return;
                    }
                    // Clear timeout immediately on successful connection
                    clearTimeout(timeoutId);
                    this.messagePort = event.data.port;
                    if (this.messagePort) {
                        this.messagePort.start();
                        // Set up persistent listener for parent requests
                        this.messagePort.addEventListener('message', async (msgEvent) => {
                            if (msgEvent.data?.type === 'REQUEST_ASSET_RETURN') {
                                // Parent is requesting asset return
                                const { retrievalId } = msgEvent.data;
                                // Return all transferred assets
                                await returnAssetsCallback();
                                // Send completion notification
                                this.messagePort?.postMessage({
                                    type: 'RETURN_COMPLETE',
                                    retrievalId
                                });
                            }
                        });
                    }
                    else {
                        console.error('[Assets SDK Child] Received ASSETS_SDK_READY but port is missing!');
                    }
                    window.removeEventListener('message', readyListener);
                    resolve(this.messagePort);
                }
            };
            window.addEventListener('message', readyListener);
            // Initial handshake can use '*' (no sensitive data), port transfer is secure
            window.parent.postMessage({ type: 'ASSETS_SDK_INIT' }, '*');
            // Timeout after 5 seconds
            timeoutId = setTimeout(() => {
                console.error('[Assets SDK Child] MessageChannel initialization timeout - parent did not respond within 5 seconds');
                if (typeof window !== 'undefined') {
                    window.removeEventListener('message', readyListener);
                }
                reject(new Error('MessageChannel initialization timeout'));
            }, 5000);
        });
    }
    /**
     * Forces all connected children to return assets
     */
    async forceAssetRetrieval() {
        // Skip retrieval if not using transferables (assets were cloned, not transferred)
        if (!this.options.useTransferables) {
            return;
        }
        const retrievalPromises = [];
        // Request asset return from all workers
        for (const [workerId, port] of this.workers.entries()) {
            const retrievalId = generateUniqueId();
            const receivedAssets = new Set(); // Track received return IDs
            const workerPromise = new Promise((resolve) => {
                const timeout = setTimeout(() => {
                    port.removeEventListener('message', completionHandler);
                    console.warn(`[Assets SDK Parent] Worker ${workerId} did not complete asset return within timeout`);
                    resolve();
                }, 3000);
                const completionHandler = (event) => {
                    // Track individual asset returns
                    if (event.data?.type === 'RETURN_ASSET') {
                        const { returnId } = event.data;
                        if (returnId) {
                            receivedAssets.add(returnId);
                        }
                    }
                    // Wait for completion signal
                    else if (event.data?.type === 'RETURN_COMPLETE' && event.data.retrievalId === retrievalId) {
                        clearTimeout(timeout);
                        port.removeEventListener('message', completionHandler);
                        resolve();
                    }
                };
                port.addEventListener('message', completionHandler);
                port.postMessage({
                    type: 'REQUEST_ASSET_RETURN',
                    retrievalId
                });
            });
            retrievalPromises.push(workerPromise);
        }
        // Request asset return from all iframes
        for (const [, port] of this.messageChannels.entries()) {
            const retrievalId = generateUniqueId();
            const receivedAssets = new Set(); // Track received return IDs
            const iframePromise = new Promise((resolve) => {
                const timeout = setTimeout(() => {
                    port.removeEventListener('message', completionHandler);
                    console.warn(`[Assets SDK Parent] Iframe did not complete asset return within timeout`);
                    resolve();
                }, 3000);
                const completionHandler = (event) => {
                    // Track individual asset returns
                    if (event.data?.type === 'RETURN_ASSET') {
                        const { returnId } = event.data;
                        if (returnId) {
                            receivedAssets.add(returnId);
                        }
                    }
                    // Wait for completion signal
                    else if (event.data?.type === 'RETURN_COMPLETE' && event.data.retrievalId === retrievalId) {
                        clearTimeout(timeout);
                        port.removeEventListener('message', completionHandler);
                        resolve();
                    }
                };
                port.addEventListener('message', completionHandler);
                port.postMessage({
                    type: 'REQUEST_ASSET_RETURN',
                    retrievalId
                });
            });
            retrievalPromises.push(iframePromise);
        }
        if (retrievalPromises.length === 0) {
            return;
        }
        await Promise.all(retrievalPromises);
    }
    /**
     * Gets the MessagePort for child (worker or iframe)
     */
    getMessagePort() {
        return this.messagePort;
    }
    /**
     * Gets the worker ID (set by parent during registration)
     */
    getWorkerId() {
        return this.workerId;
    }
    /**
     * Cleanup all channels and listeners
     */
    dispose() {
        // Close post message listener (parent-side)
        if (this.postMessageListener && typeof window !== 'undefined') {
            window.removeEventListener('message', this.postMessageListener);
            this.postMessageListener = undefined;
        }
        // Close registration channel (parent-side)
        if (this.registrationChannel) {
            this.registrationChannel.close();
            this.registrationChannel = undefined;
        }
        // Close all worker ports (parent-side)
        if (this.workers.size > 0) {
            for (const port of this.workers.values()) {
                port.close();
            }
            this.workers.clear();
        }
        // Close all iframe ports (parent-side)
        if (this.messageChannels.size > 0) {
            for (const port of this.messageChannels.values()) {
                port.close();
            }
            this.messageChannels.clear();
        }
        // Close MessagePort (child-side)
        if (this.messagePort) {
            this.messagePort.close();
            this.messagePort = undefined;
        }
        // Clear worker initialization timeout and listener (worker-side)
        if (this.workerTimeoutId) {
            clearTimeout(this.workerTimeoutId);
            this.workerTimeoutId = undefined;
        }
        if (this.workerMessageHandler && typeof self !== 'undefined') {
            self.removeEventListener('message', this.workerMessageHandler);
            this.workerMessageHandler = undefined;
        }
        if (this.workerPromiseReject) {
            this.workerPromiseReject(new Error('Assets SDK disposed before worker initialization completed'));
            this.workerPromiseReject = undefined;
        }
    }
}
//# sourceMappingURL=message-channel-manager.js.map